import { formatParisDateTime, type OperationPreview } from '@flux/shared';
import type { JSX } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { Button } from '@/components/ui/button.js';
import { EmptyState } from '@/components/ui/empty-state.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.js';

export interface OperationPreviewPanelProps {
  preview: OperationPreview | undefined;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string | undefined;
  onRun: () => void;
}

/**
 * Simulation (`02` §10, bouton historiquement « Prévisualiser ») : rejoue les
 * conditions et origines du formulaire — enregistrées ou non — sur les
 * commandes des 30 derniers jours en base, sans rien modifier ni envoyer. La
 * période de l'opération n'entre pas en jeu. Ce n'est pas une liste paginée
 * côté serveur (max 50 lignes, `OperationPreview.sample`) donc pas de
 * `<DataTable>` ici — de simples primitives `ui/table.tsx`.
 */
export function OperationPreviewPanel({
  preview,
  isLoading,
  isError,
  errorMessage,
  onRun,
}: OperationPreviewPanelProps): JSX.Element {
  return (
    <PageSection
      title="Simulation sur les 30 derniers jours"
      description="Teste les conditions et origines ci-dessus, même non enregistrées, sur les commandes reçues ces 30 derniers jours : vous voyez lesquelles auraient reçu les produits ajoutés. La période de l’opération n’est pas prise en compte, et rien n’est modifié ni envoyé."
      flush={preview !== undefined && !isLoading && !isError}
      actions={
        <Button type="button" variant="outline" isLoading={isLoading} onClick={onRun}>
          Lancer la simulation
        </Button>
      }
    >
      <SimulationResult
        preview={preview}
        isLoading={isLoading}
        isError={isError}
        errorMessage={errorMessage}
      />
    </PageSection>
  );
}

function SimulationResult({
  preview,
  isLoading,
  isError,
  errorMessage,
}: Omit<OperationPreviewPanelProps, 'onRun'>): JSX.Element | null {
  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Simulation en cours…</p>;
  }
  if (isError) {
    return (
      <p role="alert" className="text-sm text-status-red-fg">
        {errorMessage ?? 'Impossible de lancer la simulation.'}
      </p>
    );
  }
  if (!preview) {
    return null;
  }

  return (
    <>
      <p className="px-5 py-3 text-sm font-medium tabular-nums">
        {preview.total === 0
          ? 'Aucune commande des 30 derniers jours n’aurait reçu les produits ajoutés.'
          : `${String(preview.total)} commande${preview.total > 1 ? 's' : ''} des 30 derniers jours ${preview.total > 1 ? 'auraient' : 'aurait'} reçu les produits ajoutés${preview.total > preview.sample.length && preview.sample.length > 0 ? ` (${String(preview.sample.length)} premières affichées)` : ''}.`}
      </p>
      {preview.sample.length === 0 ? (
        <EmptyState
          title="Aucune commande ne correspond"
          description="Élargissez les conditions ou les origines, puis relancez la simulation."
          className="border-t border-border py-6"
        />
      ) : (
        <div className="border-t border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Numéro</TableHead>
                <TableHead>Boutique</TableHead>
                <TableHead>Payée le</TableHead>
                <TableHead className="text-right">Montant</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {preview.sample.map((item) => (
                <TableRow key={item.orderId}>
                  <TableCell className="whitespace-nowrap font-medium">{item.name}</TableCell>
                  <TableCell>{item.store}</TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums">
                    {item.paidAt ? formatParisDateTime(new Date(item.paidAt)) : '—'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {item.totalInclTax ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );
}
